import { useEffect, useState, useCallback,useRef } from "react";
import axios from "axios";
import AdminVehicleMap from "../../Component/FleetTracking/vehicleMap";
import VehicleSidebar from "../../Component/FleetTracking/vehicleSidebar";
import VehicleDetailsPanel from "../../Component/FleetTracking/VehicleDetailsPanel";
import FilterControls from "../../Component/FleetTracking/FilterControls";
import { useWebSocket } from "../../context/WebSocketContext";
import {
  getStatesForVehicles,
  getUniqueStates,
} from "../../Services/geocodingService";
import "./FleetDashboard.css";

const FleetDashboard = () => {
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, moving, stopped
  const [stateFilter, setStateFilter] = useState("all"); // all or specific state
  const [availableStates, setAvailableStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);

  const { messages } = useWebSocket();

  // Initial API load
  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-tracking`);
      console.log("Fetched vehicles:", response.data);
      
      let vehicleData = response.data.vehicles || [];
      
      // Add state information to vehicles
      vehicleData = await getStatesForVehicles(vehicleData);
      
      setVehicles(vehicleData);
      
      // Extract unique states for filter dropdown
      const states = getUniqueStates(vehicleData);
      setAvailableStates(states);
      
      setLoading(false);
    } catch (err) {
      console.error("Failed to load vehicles", err);
      setError("Failed to load vehicle data");
      setLoading(false);
    }
  };

  // WebSocket updates
  useEffect(() => {
    if (!messages.length) return;

    const latestMessage = messages[messages.length - 1];

    if (latestMessage.type === "vehicle-tracking-update") {
      const updateVehicles = async () => {
        let vehicleData = latestMessage.vehicles || [];
        vehicleData = await getStatesForVehicles(vehicleData);
        setVehicles(vehicleData);
        const states = getUniqueStates(vehicleData);
        setAvailableStates(states);
      };
      updateVehicles();
    }
  }, [messages]);

  // Apply filters
  useEffect(() => {
    let filtered = [...vehicles];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter((vehicle) =>
        vehicle.vehicleregnumber
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter === "moving") {
      filtered = filtered.filter((vehicle) => vehicle.speed > 0);
    } else if (statusFilter === "stopped") {
      filtered = filtered.filter((vehicle) => vehicle.speed === 0);
    }

    // State filter
    if (stateFilter !== "all") {
      filtered = filtered.filter((vehicle) => vehicle.state === stateFilter);
    }

    setFilteredVehicles(filtered);
  }, [vehicles, searchQuery, statusFilter, stateFilter]);

  // Focus map on filtered vehicles when state filter changes
  useEffect(() => {
    if (stateFilter !== "all" && mapRef.current && filteredVehicles.length > 0) {
      mapRef.current.focusOnVehicles(filteredVehicles);
    }
  }, [stateFilter, filteredVehicles]);

  const handleVehicleSelect = useCallback((vehicle) => {
    setSelectedVehicle(vehicle);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setSelectedVehicle(null);
  }, []);

  const handleRefresh = () => {
    loadVehicles();
  };

  const movingCount = vehicles.filter((v) => v.speed > 0).length;
  const stoppedCount = vehicles.filter((v) => v.speed === 0).length;

  return (
    <div className="fleet-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">Fleet Tracking Dashboard</h1>
          <div className="header-stats">
            <div className="stat-card">
              <span className="stat-label">Total Vehicles</span>
              <span className="stat-value">{vehicles.length}</span>
            </div>
            <div className="stat-card success">
              <span className="stat-label">Moving</span>
              <span className="stat-value">{movingCount}</span>
            </div>
            <div className="stat-card danger">
              <span className="stat-label">Stopped</span>
              <span className="stat-value">{stoppedCount}</span>
            </div>
          </div>
          <button className="refresh-btn" onClick={handleRefresh}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      <FilterControls
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        stateFilter={stateFilter}
        setStateFilter={setStateFilter}
        availableStates={availableStates}
      />

      {/* Main Content */}
      <div className="dashboard-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading vehicles...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <p>{error}</p>
            <button onClick={handleRefresh}>Retry</button>
          </div>
        ) : (
          <>
            {/* Sidebar */}
            <VehicleSidebar
              vehicles={filteredVehicles}
              selectedVehicle={selectedVehicle}
              onVehicleSelect={handleVehicleSelect}
            />

            {/* Map */}
            <div className="map-container">
              <AdminVehicleMap
                ref={mapRef}
                vehicles={filteredVehicles}
                selectedVehicle={selectedVehicle}
                onVehicleSelect={handleVehicleSelect}
              />
            </div>

            {/* Details Panel */}
            {selectedVehicle && (
              <VehicleDetailsPanel
                vehicle={selectedVehicle}
                onClose={handleCloseDetails}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FleetDashboard;  